/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import { Label, Input, Button, Text, Description } from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import { useShowError } from "~/components/Error/useShowError";
import { Fieldset } from "~/components/Forms";
import { DetailsTitle } from "~/components/Styled/Common";
import { showSuccess } from "~/components/Toast";
import {
  generateActionSuccessText,
  useAxios,
  toFraction,
  toPercentage,
} from "~/tools";
import { Level } from "./types";

const StyledFieldset = styled(Fieldset)`
  min-height: 7.5rem;
  width: 8.5rem;
  margin-left: 1rem;
`;

const StyledText = styled(Text)`
  margin-left: 1rem;
`;
type ServiceLevelGlobalProps = {
  level: Level;
};

type Values = {
  minPercentage: string;
  maxPercentage: string;
  defaultPercentage: string;
};
function ServiceLevelGlobal({ level }: ServiceLevelGlobalProps) {
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const onSubmit = async (values: Values) => {
    const organizeValues = {
      minPercentage: toFraction(values.minPercentage),
      defaultPercentage: toFraction(values.defaultPercentage),
      maxPercentage: toFraction(values.maxPercentage),
    };
    try {
      await Axios().put(`/servicelevels/${level.id}`, organizeValues);
      showSuccess(generateActionSuccessText(`Level`)()("updated")());
    } catch (e) {
      showError(e.response);
    }
  };

  const { register, handleSubmit, errors, watch } = useForm();

  const watcher = watch();

  const isBiggerThan = (value: string, compare: string) => {
    if (typeof value !== "string" || typeof compare !== "string") return false;
    if (Number(value) > Number(compare)) {
      return false;
    }

    return true;
  };

  const isSmallerThan = (value: string, compare: string) => {
    if (typeof value !== "string" || typeof compare !== "string") return false;
    if (Number(value) < Number(compare)) {
      return false;
    }

    return true;
  };

  return (
    <FlexContainer css={{ paddingTop: "1rem" }} align="flex-start">
      <DetailsTitle
        css={{
          minWidth: "6rem",
          marginTop: "2rem",
          textTransform: "capitalize",
        }}
      >
        {level.name}
      </DetailsTitle>
      <StyledFieldset>
        <Label name="Min" />
        <FlexContainer>
          <Input
            type="number"
            name="minPercentage"
            defaultValue={toPercentage(level.capacityBasedFee.minPercentage)}
            placeholder="min"
            status={errors.minPercentage && "danger"}
            ref={register({
              required: true,
              validate: (value) =>
                isBiggerThan(value, watcher.defaultPercentage),
            })}
          />
          <StyledText tag="div">%</StyledText>
        </FlexContainer>
        {errors.minPercentage && <Description>Invalid Value</Description>}
      </StyledFieldset>
      <StyledFieldset>
        <Label name="Max" />
        <FlexContainer>
          <Input
            type="number"
            name="maxPercentage"
            defaultValue={toPercentage(level.capacityBasedFee.maxPercentage)}
            placeholder="max"
            status={errors.maxPercentage && "danger"}
            ref={register({
              required: true,
              validate: (value) =>
                isSmallerThan(value, watcher.defaultPercentage),
            })}
          />
          <StyledText tag="div">%</StyledText>
        </FlexContainer>
        {errors.maxPercentage && <Description>Invalid Value</Description>}
      </StyledFieldset>
      <StyledFieldset>
        <Label name="Default" />
        <FlexContainer>
          <Input
            type="number"
            name="defaultPercentage"
            defaultValue={toPercentage(
              level.capacityBasedFee.defaultPercentage
            )}
            placeholder="default"
            status={errors.defaultPercentage && "danger"}
            ref={register({
              required: true,
              validate: (value) => isBiggerThan(value, watcher.maxPercentage),
            })}
          />
          <StyledText tag="div">%</StyledText>
        </FlexContainer>
        {errors.defaultPercentage && <Description>Invalid Value</Description>}
      </StyledFieldset>
      <StyledFieldset>
        <Button
          icon="save"
          severity="low"
          onClick={handleSubmit(onSubmit)}
          css={{ marginTop: "2rem", marginLeft: "3rem" }}
        >
          Save
        </Button>
      </StyledFieldset>
    </FlexContainer>
  );
}
export { ServiceLevelGlobal };
